import { forwardRef, Ref } from "react";
import { faInfoCircle } from "@fortawesome/pro-duotone-svg-icons";
import { faCaretDown, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactSelect, {
  ClearIndicatorProps,
  DropdownIndicatorProps,
  GroupBase,
  MultiValueRemoveProps,
  Props,
  SelectInstance,
  components,
} from "react-select";
import { VariantProps, tv } from "tailwind-variants";

const styles = tv({
  slots: {
    container: "group relative flex w-full flex-col gap-10",
    label:
      "absolute -top-10 left-10 z-20 bg-white px-10 !text-caption opacity-100 transition-all duration-100 group-[&:has(#react-select-3-placeholder)]:top-[23px] group-[&:has(#react-select-3-placeholder)]:opacity-0",
    control: "rounded-sm border border-gray bg-white hover:cursor-pointer",
    focusedControl:
      "rounded-sm border border-secondary bg-white hover:cursor-pointer",
    disabledControl:
      "rounded-sm border border-gray bg-gray-light hover:cursor-pointer",
    placeholder: "!text-caption text-secondary",
    selectInput: "py-5",
    valueContainer: "gap-x-10 py-10 pl-20",
    multiValue:
      "items-center gap-10 rounded bg-gray-light px-10 py-5 text-secondary",
    multiValueRemove:
      "rounded border border-gray bg-white p-5 text-secondary transition-colors hover:border-red-light hover:text-red",
    indicatorsContainer: "gap-10 px-20 py-15",
    clearIndicator: "text-gray-dark transition-colors hover:text-red",
    dropdownIndicator: "text-gray-dark transition-colors hover:text-secondary",
    indicatorSeparator: "bg-gray",
    menu: "!z-50 mt-5 rounded-sm border border-gray bg-white text-base",
    groupHeading: "mb-5 mt-20 px-20 !text-small uppercase text-gray-dark",
    option:
      "px-20 py-5 transition-colors hover:cursor-pointer hover:bg-primary-light",
    focusedOption:
      "bg-primary-light px-20 py-5 transition-colors hover:cursor-pointer hover:bg-primary-light",
    selectedOption:
      "bg-primary px-20 py-5 text-white transition-colors hover:cursor-pointer hover:bg-primary-light hover:text-secondary",
    noOptionsMessage: "py-10 text-gray-dark",
    helperText: "flex flex-row items-center gap-10 !text-caption",
  },
  variants: {
    variant: {
      error: {
        control: "border-red text-red",
        focusedControl: "border-red text-red",
        placeholder: "text-red",
        label: "text-red",
        helperText: "text-red",
      },
      warning: {
        control: "border-orange text-orange",
        focusedControl: "border-orange text-orange",
        placeholder: "text-orange",
        label: "text-orange",
        helperText: "text-orange",
      },
      success: {
        control: "border-green text-green",
        focusedControl: "border-green text-green",
        placeholder: "text-green",
        label: "text-green",
        helperText: "text-green",
      },
    },
  },
});

const DropdownIndicator = (props: DropdownIndicatorProps) => (
  <components.DropdownIndicator {...props}>
    <FontAwesomeIcon fixedWidth icon={faCaretDown} />
  </components.DropdownIndicator>
);

const ClearIndicator = (props: ClearIndicatorProps) => {
  return (
    <components.ClearIndicator {...props}>
      <FontAwesomeIcon fixedWidth icon={faTimes} />
    </components.ClearIndicator>
  );
};

const MultiValueRemove = (props: MultiValueRemoveProps) => {
  return (
    <components.MultiValueRemove {...props}>
      <FontAwesomeIcon fixedWidth icon={faTimes} />
    </components.MultiValueRemove>
  );
};

export interface IDropdownOption {
  label: string;
  value: string;
}

type SelectProps = Props & {
  label: string;
  name: string;
  variant?: VariantProps<typeof styles>["variant"];
  helperText?: string;
  testId?: string;
};

const Select = forwardRef(function Select(
  { label, name, variant, helperText, testId, ...props }: SelectProps,
  ref: Ref<SelectInstance<unknown, boolean, GroupBase<unknown>>> | null,
) {
  const {
    container,
    label: labelStyles,
    control,
    focusedControl,
    disabledControl,
    placeholder,
    selectInput,
    valueContainer,
    multiValue,
    multiValueRemove,
    indicatorsContainer,
    clearIndicator,
    dropdownIndicator,
    indicatorSeparator,
    menu,
    groupHeading,
    option,
    selectedOption,
    focusedOption,
    noOptionsMessage,
    helperText: helperTextStyles,
  } = styles({ variant });

  const renderHelperText = () =>
    helperText && (
      <p className={helperTextStyles()}>
        <FontAwesomeIcon icon={faInfoCircle} />
        {helperText}
      </p>
    );

  return (
    <div className={container()}>
      <label htmlFor={name} className={labelStyles()}>
        {label}
      </label>
      <ReactSelect
        unstyled
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        name={name}
        placeholder={label}
        components={{ DropdownIndicator, ClearIndicator, MultiValueRemove }}
        classNames={{
          control: ({ isFocused, isDisabled, hasValue }) =>
            isFocused || hasValue
              ? focusedControl()
              : isDisabled
                ? disabledControl()
                : control(),
          placeholder: () => placeholder(),
          input: () => selectInput(),
          valueContainer: () => valueContainer(),
          multiValue: () => multiValue(),
          multiValueRemove: () => multiValueRemove(),
          indicatorsContainer: () => indicatorsContainer(),
          clearIndicator: () => clearIndicator(),
          dropdownIndicator: () => dropdownIndicator(),
          indicatorSeparator: () => indicatorSeparator(),
          menu: () => menu(),
          groupHeading: () => groupHeading(),
          option: ({ isSelected, isFocused }) =>
            isSelected
              ? selectedOption()
              : isFocused
                ? focusedOption()
                : option(),
          noOptionsMessage: () => noOptionsMessage(),
        }}
        data-testid={testId}
        ref={ref}
        {...props}
      />
      {renderHelperText()}
    </div>
  );
});

export default Select;
